import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/marketplaceTrack";
import { MarketHero } from "@/components/marketplace/MarketHero";
import { HeroCarousel } from "@/components/marketplace/HeroCarousel";
import { MarketSection } from "@/components/marketplace/MarketSection";
import { CategoryBrowser } from "@/components/marketplace/CategoryBrowser";

const DESC = "HOH_Vibes Marketplace — gospel music, church website builder, hymn projection, attendance, e-books, Power BI templates and merchandise.";

function useSeo(data) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "HOH_Vibes Marketplace | Music, Church Software, Templates & Merch";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.setAttribute("content", DESC);
    return () => { document.title = prevTitle; if (prevDesc) meta.setAttribute("content", prevDesc); };
  }, []);
  useEffect(() => {
    if (!data) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "ItemList", name: "HOH_Vibes Marketplace",
      itemListElement: data.featured.map((l, i) => ({ "@type": "ListItem", position: i + 1, name: l.title, description: l.description })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [data]);
}

const linkAll = (to, label, testid) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#D4AF37] transition-colors">{label} <ArrowRight size={15} /></Link>
);

export default function MarketplaceHome() {
  const [data, setData] = useState(null);
  const visited = useRef(false);
  useSeo(data);

  useEffect(() => {
    if (!visited.current) { visited.current = true; track("visit"); }
    api.get("/marketplace/home").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  const catName = useMemo(() => {
    const m = Object.fromEntries((data?.categories || []).map((c) => [c.key, c.name]));
    return (k) => m[k] || k;
  }, [data]);

  if (data?.error) return <div className="max-w-xl mx-auto py-32 text-center text-neutral-400" data-testid="mkt-error">The marketplace is unavailable right now. Please try again shortly.</div>;

  return (
    <div data-testid="marketplace-home" className="bg-[#0A0A0A]">
      {data?.slides?.length ? <HeroCarousel slides={data.slides} /> : <MarketHero hero={data?.hero} spotlight={data?.featured?.[0]} />}
      {data && (
        <>
          <MarketSection id="featured" index="01" eyebrow="Featured" title="Handpicked for" accent="your ministry" items={data.featured} catName={catName}
            intro="Our flagship products and services — chosen to serve churches, creators and teams." />
          <CategoryBrowser categories={data.categories} listings={data.all} catName={catName} />
          <MarketSection id="new-releases" index="03" eyebrow="New Releases" title="Fresh from the" accent="studio" items={data.new_releases} catName={catName}
            action={linkAll("/music", "All music", "mkt-all-music")} />
          <MarketSection id="subscriptions" index="04" eyebrow="Subscriptions" title="Software that" accent="grows with you" items={data.subscriptions} catName={catName}
            tone="border-t border-white/5 bg-gradient-to-b from-[#0F172A]/40 to-transparent" intro="Every subscription starts with a free trial. Cancel anytime." />
          <MarketSection id="merchandise" index="05" eyebrow="Merchandise" title="Wear the" accent="message" items={data.merchandise} catName={catName}
            action={linkAll("/merch", "Merch gallery", "mkt-all-merch")} />
          <MarketSection id="coming-soon" index="06" eyebrow="Coming Soon" title="What we're" accent="building next" items={data.coming_soon} catName={catName}
            tone="border-t border-white/5" />
        </>
      )}
    </div>
  );
}
